import { useState, useEffect } from 'react'

const STORAGE_KEY = 'vzla_offer_expiry'
const COOKIE_NAME = 'vzla_offer_expiry'
const DURATION_HOURS = 72
const DURATION_SECONDS = DURATION_HOURS * 60 * 60

// Cookie helpers (backup only — may be blocked in iframes)
function setCookie(name: string, value: string, days: number) {
  try {
    if (typeof document === 'undefined') return
    const date = new Date()
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
    const expires = `expires=${date.toUTCString()}`
    document.cookie = `${name}=${value};${expires};path=/;SameSite=None;Secure`
  } catch {
    // Cookie write may fail in sandboxed iframes — that's fine
  }
}

function getCookie(name: string): string | null {
  try {
    if (typeof document === 'undefined') return null
    const nameEQ = `${name}=`
    const ca = document.cookie.split(';')
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) === ' ') c = c.substring(1, c.length)
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
    }
  } catch {
    // Cookie read may fail — that's fine
  }
  return null
}

function deleteCookie(name: string) {
  try {
    if (typeof document === 'undefined') return
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
  } catch {
    // Ignore
  }
}

// Read expiry from localStorage (primary) or cookie (fallback)
function readExpiry(): number | null {
  // 1. Try localStorage first (most reliable in iframes)
  try {
    const localVal = localStorage.getItem(STORAGE_KEY)
    if (localVal) {
      const parsed = parseInt(localVal, 10)
      if (!isNaN(parsed) && parsed > 0) return parsed
    }
  } catch {
    // localStorage may be unavailable
  }

  // 2. Fallback to cookie
  const cookieVal = getCookie(COOKIE_NAME)
  if (cookieVal) {
    const parsed = parseInt(cookieVal, 10)
    if (!isNaN(parsed) && parsed > 0) {
      // Restore to localStorage if possible
      try {
        localStorage.setItem(STORAGE_KEY, cookieVal)
      } catch {}
      return parsed
    }
  }

  return null
}

// Write expiry to both stores
function writeExpiry(expiryTime: number) {
  const val = expiryTime.toString()
  try {
    localStorage.setItem(STORAGE_KEY, val)
  } catch {}
  setCookie(COOKIE_NAME, val, 7)
}

// Clear both stores
function clearExpiry() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {}
  deleteCookie(COOKIE_NAME)
}

export function useOfferTimer() {
  const [timeLeft, setTimeLeft] = useState<number>(0)
  const [isExpired, setIsExpired] = useState<boolean>(false)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  useEffect(() => {
    if (typeof window === 'undefined') return

    // Check for admin reset parameter
    const urlParams = new URLSearchParams(window.location.search)
    if (urlParams.get('reset_timer') === 'true') {
      clearExpiry()
      // Clean up URL without reloading
      window.history.replaceState({}, document.title, window.location.pathname)
    }

    // Read existing expiry or create new one
    let expiryTime = readExpiry()
    const now = Math.floor(Date.now() / 1000)

    if (!expiryTime) {
      // First visit or after reset: set new expiry 72h from now
      expiryTime = now + DURATION_SECONDS
      writeExpiry(expiryTime)
    }

    // Calculate initial time left
    const initialTimeLeft = Math.max(0, expiryTime - now)
    setTimeLeft(initialTimeLeft)
    setIsExpired(initialTimeLeft <= 0)
    setIsInitialized(true)

    // Start countdown
    const intervalId = setInterval(() => {
      const currentNow = Math.floor(Date.now() / 1000)
      const remaining = Math.max(0, expiryTime! - currentNow)
      setTimeLeft(remaining)

      if (remaining <= 0) {
        setIsExpired(true)
        clearInterval(intervalId)
      }
    }, 1000)

    return () => clearInterval(intervalId)
  }, [])

  // Format time
  const h = Math.floor(timeLeft / 3600)
  const m = Math.floor((timeLeft % 3600) / 60)
  const s = timeLeft % 60

  const formattedTime = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`

  return {
    timeLeft,
    isExpired,
    formattedTime,
    isInitialized,
    hours: h.toString().padStart(2, '0'),
    minutes: m.toString().padStart(2, '0'),
    seconds: s.toString().padStart(2, '0'),
  }
}
